/**
 * Masonry and directory columns and gap, per device.
 *
 * Desktop keeps the block's own `columns` and `gap`; tablet and phone store
 * only what differs, in `block.devices`. A device without its own value takes
 * the one above it (phone from tablet, tablet from desktop). Columns taken
 * that way are capped by what the block's old width breakpoints gave at a
 * typical tablet and phone width, so a five-column desktop grid still narrows
 * on smaller screens unless a device is told otherwise.
 *
 *   block.devices = { tablet?: { columns?, gap? }, phone?: { columns?, gap? } }
 *
 * Pure, so the server render, the editor and tests share it.
 */

import { getColumnCount } from '../masonry/utils.js';

export const GALLERY_DEVICES = ['base', 'tablet', 'phone'];
export const GALLERY_COLUMNS = { min: 1, max: 6, fallback: 5 };
export const GALLERY_GAP_FALLBACK = 4;

const TYPICAL_WIDTH = { tablet: 820, phone: 390 };
const PARENT = { tablet: 'base', phone: 'tablet' };

const deviceOf = (device) => (GALLERY_DEVICES.includes(device) ? device : 'base');

const wholeColumns = (value, fallback) => {
    const n = Math.round(Number(value));
    return Number.isFinite(n) && n > 0
        ? Math.max(GALLERY_COLUMNS.min, Math.min(GALLERY_COLUMNS.max, n))
        : fallback;
};

/** The value a device stores itself, or undefined. */
function own(block, device, field) {
    const at = deviceOf(device);
    if (at === 'base') return block?.[field] ?? undefined;
    return block?.devices?.[at]?.[field];
}

/** True when tablet or phone has its own value for `field`. */
export function galleryOverridden(block, device, field) {
    return deviceOf(device) !== 'base' && own(block, device, field) !== undefined;
}

/** Columns a device shows. */
export function galleryColumns(block, device) {
    const at = deviceOf(device);
    const base = wholeColumns(block?.columns, GALLERY_COLUMNS.fallback);
    if (at === 'base') return base;
    const mine = own(block, at, 'columns');
    if (mine !== undefined) return wholeColumns(mine, base);
    const above = galleryColumns(block, PARENT[at]);
    const narrowed = getColumnCount(TYPICAL_WIDTH[at], block?.breakpoints || null, base);
    return Math.max(GALLERY_COLUMNS.min, Math.min(above, narrowed));
}

/** Stored gap a device uses: its own, else the one above it. */
export function galleryGap(block, device) {
    const at = deviceOf(device);
    const mine = own(block, at, 'gap');
    if (mine !== undefined && mine !== null) return mine;
    return at === 'base' ? GALLERY_GAP_FALLBACK : galleryGap(block, PARENT[at]);
}

/** Columns and gap for every device, for renderers that pick one at runtime. */
export function galleryDeviceLayout(block) {
    return Object.fromEntries(GALLERY_DEVICES.map((device) => [
        device,
        { columns: galleryColumns(block, device), gap: galleryGap(block, device) },
    ]));
}

/**
 * Patch for one field on one device; undefined clears a tablet or phone value
 * so it inherits again. Empty devices drop out of `devices`.
 * @returns {Object}
 */
export function setGalleryValue(block, device, field, value) {
    const at = deviceOf(device);
    if (at === 'base') return { [field]: value };
    const devices = { ...(block?.devices || {}) };
    const entry = { ...(devices[at] || {}) };
    if (value === undefined) delete entry[field];
    else entry[field] = value;
    if (Object.keys(entry).length) devices[at] = entry;
    else delete devices[at];
    return { devices: Object.keys(devices).length ? devices : undefined };
}
